// src/features/ads/adsSlice.ts
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Ad } from "./adsTypes";
import { createAd, fetchAdById, fetchAds, updateAd } from "./adsThunks";
import i18n from "../../i18n";
import { getLocalizedText } from "./i18nFields";

interface AdsState {
  items: Ad[];
  currentAd: Ad | null;
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  createSuccess: boolean;
  hasMore: boolean;
  nextCursor: string | null;
  fetchAdsRequestId: string | null;
}

const initialState: AdsState = {
  items: [],
  currentAd: null,
  loading: false,
  loadingMore: false,
  error: null,
  createSuccess: false,
  hasMore: true,
  nextCursor: null,
  fetchAdsRequestId: null,
};

const adsSlice = createSlice({
  name: "ads",
  initialState,
  reducers: {
    resetAdsState(state) {
      state.error = null;
      state.createSuccess = false;
      state.loadingMore = false;
      state.hasMore = true;
      state.nextCursor = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createAd.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.createSuccess = false;
      })
      .addCase(createAd.fulfilled, (state, action: PayloadAction<Ad>) => {
        state.loading = false;
        state.items.push(action.payload);
        state.createSuccess = true;
      })
      .addCase(createAd.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) ?? i18n.t("system.unknownError");
        state.createSuccess = false;
      })
      .addCase(updateAd.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.createSuccess = false;
      })
      .addCase(updateAd.fulfilled, (state, action: PayloadAction<Ad>) => {
        state.loading = false;
        state.currentAd = action.payload;
        state.items = state.items.map((item) =>
          String(item.id || "") === String(action.payload.id || "") ? action.payload : item
        );
        state.createSuccess = true;
      })
      .addCase(updateAd.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) ?? i18n.t("system.unknownError");
        state.createSuccess = false;
      })
      .addCase(fetchAds.pending, (state, action) => {
        const isLoadMore = Boolean(action.meta.arg?.cursor);
        state.loading = !isLoadMore;
        state.loadingMore = isLoadMore;
        state.error = null;
        state.fetchAdsRequestId = action.meta.requestId;
      })
      .addCase(fetchAds.fulfilled, (state, action) => {
        if (state.fetchAdsRequestId !== action.meta.requestId) return;
        state.loading = false;
        state.loadingMore = false;
        state.fetchAdsRequestId = null;
        state.hasMore = action.payload.hasMore;
        state.nextCursor = action.payload.nextCursor;

        if (action.payload.reset) {
          state.items = action.payload.items;
        } else {
          const getKey = (item: Ad) =>
            item.id || `${getLocalizedText(item.title_i18n, i18n.resolvedLanguage || "fr")}-${item.price}-${item.adress}`;
          const existing = new Set(state.items.map((item) => getKey(item)));
          const toAdd = action.payload.items.filter((item) => !existing.has(getKey(item)));
          state.items = [...state.items, ...toAdd];
        }
      })
      .addCase(fetchAds.rejected, (state, action) => {
        if (state.fetchAdsRequestId !== action.meta.requestId) return;
        state.loading = false;
        state.loadingMore = false;
        state.fetchAdsRequestId = null;
        state.error = (action.payload as string) ?? i18n.t("system.unknownError");
      })
      .addCase(fetchAdById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdById.fulfilled, (state, action: PayloadAction<Ad>) => {
        state.loading = false;
        state.currentAd = action.payload;
      })
      .addCase(fetchAdById.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) ?? i18n.t("system.unknownError");
      })
      ;
  }
});

export const { resetAdsState } = adsSlice.actions;
export default adsSlice.reducer;
